import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPANY, DESTINATIONS } from "@/lib/data";
import { trackConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  full_name: "",
  phone: "",
  email: "",
  destination: "",
  course: "",
  qualification: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const waUrl = `https://wa.me/${COMPANY.phoneRaw.replace("+", "")}?text=${encodeURIComponent(COMPANY.whatsappMsg)}`;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackConversion("Lead", {
        content_name: `${form.course} — ${form.destination}`,
        content_category: "Counselling Form",
        destination: form.destination,
        course: form.course,
        qualification: form.qualification,
        value: 1,
        currency: "INR",
      });
      toast.success("Consultation booked!", {
        description: "Our counsellor will contact you within 24 hours.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Submission failed", { description: "Please try again or WhatsApp us directly." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Contact</div>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
              Let's map your <span className="font-serif italic text-gradient-royal">global future.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Book a free 30-minute counselling session with our expert team. Zero obligation, 100% clarity.
            </p>

            <div className="mt-8 space-y-4">
              <ContactRow icon={Phone} label="Call us" value={COMPANY.phone} href={`tel:${COMPANY.phoneRaw}`} testid="contact-phone" />
              <ContactRow icon={Mail} label="Email us" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-email" />
              <ContactRow icon={MapPin} label="Visit us" value={COMPANY.address} testid="contact-address" />
              <ContactRow icon={MessageCircle} label="WhatsApp" value="Chat with us instantly" href={waUrl} external testid="contact-whatsapp" />
            </div>

            <div className="mt-8 rounded-3xl overflow-hidden border border-border h-64">
              <iframe
                title="Globe Vista Location"
                src="https://www.google.com/maps?q=Santosh+Nagar+Hyderabad&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-maps"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              data-testid="counselling-form"
              className="rounded-3xl border border-border bg-card p-6 md:p-10 shadow-xl"
            >
              <h3 className="text-2xl font-heading font-semibold">Book Free Consultation</h3>
              <p className="text-sm text-muted-foreground mt-1">Fill this and we'll call you back within 24 hours.</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *">
                  <Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="John Doe" data-testid="form-name" />
                </Field>
                <Field label="Phone Number *">
                  <Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" data-testid="form-phone" />
                </Field>
                <Field label="Email *">
                  <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="form-email" />
                </Field>
                <Field label="Destination Country *">
                  <Select value={form.destination} onValueChange={(v) => set("destination", v)} required>
                    <SelectTrigger data-testid="form-destination"><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent>
                      {DESTINATIONS.map((d) => (
                        <SelectItem key={d.country} value={d.country}>{d.country}</SelectItem>
                      ))}
                      <SelectItem value="Not Sure">Not Sure Yet</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Preferred Course *">
                  <Input required value={form.course} onChange={(e) => set("course", e.target.value)} placeholder="MS in Computer Science" data-testid="form-course" />
                </Field>
                <Field label="Current Qualification *">
                  <Select value={form.qualification} onValueChange={(v) => set("qualification", v)} required>
                    <SelectTrigger data-testid="form-qualification"><SelectValue placeholder="Select qualification" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12th / Intermediate">12th / Intermediate</SelectItem>
                      <SelectItem value="Bachelor's Degree">Bachelor's Degree</SelectItem>
                      <SelectItem value="Master's Degree">Master's Degree</SelectItem>
                      <SelectItem value="Diploma">Diploma</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <Field label="Message" className="mt-4">
                <Textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your goals & timeline..." data-testid="form-message" />
              </Field>

              <Button
                type="submit"
                disabled={loading}
                data-testid="counselling-submit-form"
                className="mt-6 w-full rounded-full bg-[#0F3D8C] hover:bg-[#0C3275] dark:bg-[#2563EB] dark:hover:bg-[#3B82F6] text-white py-6 text-base font-semibold shadow-lg shadow-[#0F3D8C]/20"
              >
                {loading ? "Submitting..." : (<><Send className="w-4 h-4 mr-2" /> Book My Free Consultation</>)}
              </Button>

              <p className="mt-3 text-xs text-center text-muted-foreground">
                By submitting, you agree to be contacted by our counsellors.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs tracking-wider uppercase font-semibold text-muted-foreground mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testid }) {
  const inner = (
    <>
      <div className="w-11 h-11 rounded-xl bg-[#0F3D8C]/10 dark:bg-[#2563EB]/20 flex items-center justify-center text-[#0F3D8C] dark:text-[#60A5FA] shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{label}</div>
        <div className="font-medium">{value}</div>
      </div>
    </>
  );
  if (href)
    return (
      <a data-testid={testid} href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className="flex items-center gap-4 group hover:text-[#0F3D8C] dark:hover:text-[#60A5FA] transition-colors">
        {inner}
      </a>
    );
  return <div data-testid={testid} className="flex items-center gap-4">{inner}</div>;
}
